import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from '@peaky-ai/outpost-browser/react';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';

import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { AuthProvider, useAuth } from '@/lib/auth';
import { AcceptInvitePage } from '@/pages/accept-invite';
import { AlertsPage } from '@/pages/alerts';
import { IssueDetailPage } from '@/pages/issue-detail';
import { IssuesPage } from '@/pages/issues';
import { LoginPage } from '@/pages/login';
import { OAuthConsentPage } from '@/pages/oauth-consent';
import { OrganizationSettingsPage } from '@/pages/organization-settings';
import { ProjectsPage } from '@/pages/projects';
import { ReleasesPage } from '@/pages/releases';
import { SsoCallbackPage } from '@/pages/sso-callback';
import { TeamPage } from '@/pages/team';
import { SettingsPage } from '@/pages/settings';
import { UserSettingsPage } from '@/pages/user-settings';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Error data is append-only and read repeatedly; a short stale window
      // keeps navigation instant without showing anything meaningfully old.
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

export function App() {
  return (
    <ErrorBoundary fallback={(error, reset) => <CrashScreen error={error} reset={reset} />}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              {/* Public: most people opening an invitation have no account. */}
              <Route path="/invite" element={<AcceptInvitePage />} />
              {/* Where identity providers redirect back to. Public because the
                  common case is a sign-in, which has no session yet — and it
                  has to match the redirect URI registered at the provider
                  exactly, so the path cannot depend on who is asking. */}
              <Route path="/auth/sso/:provider/callback" element={<SsoCallbackPage />} />
              {/* Outside the dashboard layout: this is a decision point in
                  someone else's flow, not a page of the application. */}
              <Route
                path="/oauth/consent"
                element={
                  <RequireAuth>
                    <OAuthConsentPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <Layout />
                  </RequireAuth>
                }
              >
                <Route index element={<ProjectsPage />} />
                <Route path="projects/:projectId/issues" element={<IssuesPage />} />
                <Route path="projects/:projectId/issues/:issueId" element={<IssueDetailPage />} />
                <Route path="projects/:projectId/releases" element={<ReleasesPage />} />
                <Route path="projects/:projectId/alerts" element={<AlertsPage />} />
                <Route path="projects/:projectId/settings" element={<SettingsPage />} />
                <Route
                  path="organizations/:orgId/settings"
                  element={<OrganizationSettingsPage />}
                />
                <Route path="teams/:teamId" element={<TeamPage />} />
                <Route path="account" element={<UserSettingsPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Render nothing until the session restore settles, so a signed-in user is
  // never briefly bounced to the login screen on refresh.
  if (loading) return null;
  // Carry the destination through the login screen. Without this a signed-out
  // user starting an MCP connection would sign in and land on the project list,
  // having silently lost the consent request they came to answer.
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return <>{children}</>;
}

/** Last-resort UI when the dashboard itself throws. */
function CrashScreen({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div className="grid min-h-screen place-items-center px-4 text-center">
      <div className="max-w-md space-y-3">
        <h1 className="text-lg font-semibold">Outpost hit an error</h1>
        <p className="text-sm text-muted-foreground">{error.message}</p>
        <p className="text-xs text-muted-foreground">This has been reported to Outpost itself.</p>
        <Button onClick={reset}>Try again</Button>
      </div>
    </div>
  );
}
